import { useEffect, useState } from "react";

import { RoutineCard } from "./RoutineCard";
import { RoutineEditor } from "./RoutineEditor";
import { RoutineEmptyState } from "./RoutineEmptyState";

import {
  loadRoutines,
  saveRoutines,
} from "./routine.storage";

import type { Routine } from "./routine.types";

import "./RoutinesPage.css";

export function RoutinesPage() {
  const [routines, setRoutines] = useState<Routine[]>(
    () => loadRoutines(),
  );

  const [editorOpen, setEditorOpen] = useState(false);

  const [editingRoutine, setEditingRoutine] =
    useState<Routine | null>(null);

  useEffect(() => {
    saveRoutines(routines);
  }, [routines]);

  function openNewRoutine() {
    setEditingRoutine(null);
    setEditorOpen(true);
  }

  function openEditRoutine(routine: Routine) {
    setEditingRoutine(routine);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingRoutine(null);
  }

  function saveRoutine(routine: Routine) {
    setRoutines((current) => {
      const exists = current.some(
        (item) => item.id === routine.id,
      );

      if (!exists) {
        return [...current, routine].sort(
          (first, second) =>
            new Date(first.dueAt).getTime() -
            new Date(second.dueAt).getTime(),
        );
      }

      return current
        .map((item) =>
          item.id === routine.id
            ? routine
            : item,
        )
        .sort(
          (first, second) =>
            new Date(first.dueAt).getTime() -
            new Date(second.dueAt).getTime(),
        );
    });

    closeEditor();
  }

  function toggleRoutine(routineId: string) {
    setRoutines((current) =>
      current.map((routine) =>
        routine.id === routineId
          ? {
              ...routine,
              completed: !routine.completed,
              updatedAt: Date.now(),
            }
          : routine,
      ),
    );
  }

  function deleteRoutine(routineId: string) {
    setRoutines((current) =>
      current.filter(
        (routine) => routine.id !== routineId,
      ),
    );
  }

  const routineCount = routines.filter(
    (routine) => routine.isRoutine,
  ).length;

  const completedCount = routines.filter(
    (routine) => routine.completed,
  ).length;

  return (
    <>
      <main className="routines-page">
        <header className="routines-page__header">
          <div>
            <span>REPEAT WITH PURPOSE</span>

            <h1>Routines & tasks</h1>

            <p>
              Build consistency without making your day
              feel crowded.
            </p>
          </div>

          <button
            type="button"
            onClick={openNewRoutine}
          >
            + Add
          </button>
        </header>

        <section className="routines-page__stats">
          <article>
            <span>ROUTINES</span>
            <strong>{routineCount}</strong>
          </article>

          <article>
            <span>COMPLETED TODAY</span>
            <strong>
              {completedCount}/{routines.length}
            </strong>
          </article>

          <article>
            <span>NEXT STEP</span>
            <strong>
              {routines.find(
                (routine) => !routine.completed,
              )?.title || "All clear"}
            </strong>
          </article>
        </section>

        <p className="routines-page__section-title">
          Your routines and reminders
        </p>

        {routines.length === 0 ? (
          <RoutineEmptyState
            onAdd={openNewRoutine}
          />
        ) : (
          <section className="routines-page__list">
            {routines.map((routine) => (
              <RoutineCard
                key={routine.id}
                routine={routine}
                onToggle={toggleRoutine}
                onEdit={openEditRoutine}
                onDelete={deleteRoutine}
              />
            ))}
          </section>
        )}
      </main>

      <RoutineEditor
        open={editorOpen}
        routine={editingRoutine}
        onClose={closeEditor}
        onSave={saveRoutine}
      />
    </>
  );
}